import { Helmet } from 'react-helmet-async';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  User,
  Share2,
  List
} from 'lucide-react';
import { blogArticles } from './Blog';

const slugify = (text) => {
  return text
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’"«»]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 80);
};

const BlogArticle = () => {
  const { slug } = useParams();
  const article = blogArticles.find(a => a.slug === slug);

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://mehdi-richa.fr';
  const articleUrl = article ? `${origin}/blog/${article.slug}` : '';

  // Inject JSON-LD structured data and article-specific meta tags into <head>
  useEffect(() => {
    if (!article) return;

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.excerpt,
      "image": article.image,
      "datePublished": article.date,
      "dateModified": article.date,
      "author": {
        "@type": "Person",
        "name": "Mehdi Richa",
        "url": `${origin}/a-propos`,
        "jobTitle": "Formateur & Auditeur Qualité"
      },
      "publisher": { "@type": "Person", "name": "Mehdi Richa" },
      "mainEntityOfPage": { "@type": "WebPage", "@id": articleUrl },
      "articleSection": article.category,
      "inLanguage": "fr-FR"
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${origin}/` },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${origin}/blog` },
        { "@type": "ListItem", "position": 3, "name": article.title, "item": articleUrl }
      ]
    };

    const nodes = [];

    const addScript = (id, data) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      nodes.push(script);
    };

    const addMeta = (id, attrs) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const tag = document.createElement('meta');
      tag.id = id;
      Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
      document.head.appendChild(tag);
      nodes.push(tag);
    };

    addScript('ld-article', articleSchema);
    addScript('ld-breadcrumb', breadcrumbSchema);
    addMeta('meta-article-published', { property: 'article:published_time', content: article.date });
    addMeta('meta-article-author', { property: 'article:author', content: 'Mehdi Richa' });
    addMeta('meta-article-section', { property: 'article:section', content: article.category });

    const canonical = document.querySelector('link[rel="canonical"]');
    const prevHref = canonical ? canonical.getAttribute('href') : null;
    if (canonical) canonical.setAttribute('href', articleUrl);

    return () => {
      nodes.forEach((n) => n.remove());
      if (canonical && prevHref) canonical.setAttribute('href', prevHref);
    };
  }, [article, articleUrl, origin]);

  // Build table of contents from H2 headings and inject anchor IDs into the HTML
  const { toc, contentWithAnchors } = useMemo(() => {
    if (!article) return { toc: [], contentWithAnchors: '' };
    const tocItems = [];
    const used = new Set();
    const html = article.content.replace(
      /<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/gi,
      (match, attrs, inner) => {
        const text = inner.replace(/<[^>]+>/g, '').trim();
        let id = slugify(text);
        let suffix = 1;
        while (used.has(id)) {
          suffix += 1;
          id = `${slugify(text)}-${suffix}`;
        }
        used.add(id);
        tocItems.push({ id, text });
        const cleanAttrs = (attrs || '').replace(/\sid="[^"]*"/i, '');
        return `<h2${cleanAttrs} id="${id}">${inner}</h2>`;
      }
    );
    return { toc: tocItems, contentWithAnchors: html };
  }, [article]);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  // Get related articles (same category, excluding current)
  const relatedArticles = blogArticles
    .filter(a => a.category === article.category && a.id !== article.id)
    .slice(0, 2);

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
      if (window.history.replaceState) {
        window.history.replaceState(null, '', `#${id}`);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{article.title} | Blog Mehdi Richa</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={article.image} />
        <meta property="og:type" content="article" />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-slate-50" data-testid="article-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
              {article.category}
            </span>
            <span className="text-slate-500 text-sm flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(article.date)}
            </span>
            <span className="text-slate-500 text-sm flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {article.readTime} de lecture
            </span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
            {article.title}
          </h1>

          <p className="text-xl text-slate-600 leading-relaxed mb-8">
            {article.excerpt}
          </p>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">MR</span>
              </div>
              <div>
                <p className="font-medium text-slate-900">Mehdi Richa</p>
                <p className="text-sm text-slate-500">Formateur & Auditeur Qualité</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-white" data-testid="article-image">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative -mt-4 rounded-2xl overflow-hidden shadow-xl">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-64 sm:h-96 object-cover"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white" data-testid="article-content">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {toc.length > 1 && (
            <nav 
              aria-label="Sommaire de l'article" 
              className="mb-12 p-6 sm:p-8 bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 rounded-2xl"
              data-testid="article-toc"
            >
              <div className="flex items-center gap-2 mb-4">
                <List className="w-5 h-5 text-blue-600" />
                <h2 className="font-serif text-lg font-bold text-slate-900 m-0">
                  Sommaire
                </h2>
              </div>
              <ol className="space-y-2 list-none pl-0 counter-reset-toc">
                {toc.map((item, index) => (
                  <li 
                    key={item.id}
                    className="flex gap-3 items-start group"
                    data-testid={`toc-item-${index + 1}`}
                  >
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white border border-blue-200 text-blue-600 text-xs font-semibold flex items-center justify-center mt-0.5 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-colors">
                      {index + 1}
                    </span>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => handleTocClick(e, item.id)}
                      className="text-slate-700 hover:text-blue-600 leading-snug transition-colors"
                    >
                      {item.text}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          <article 
            className="prose prose-slate prose-lg max-w-none
              prose-headings:font-serif prose-headings:text-slate-900
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-3 prose-h2:border-b prose-h2:border-slate-200 prose-h2:scroll-mt-24
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-slate-600 prose-p:leading-relaxed prose-p:text-justify
              prose-li:text-slate-600 prose-li:leading-relaxed
              prose-ul:my-6 prose-ul:space-y-2
              prose-ol:my-6 prose-ol:space-y-3
              prose-strong:text-slate-800 prose-strong:font-semibold
              prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
              [&>p:first-of-type]:text-lg [&>p:first-of-type]:text-slate-700 [&>p:first-of-type]:font-medium
              [&>ol>li]:pl-2 [&>ul>li]:pl-2
              [&>ol]:list-decimal [&>ol]:pl-6
              [&>ul]:list-disc [&>ul]:pl-6"
            dangerouslySetInnerHTML={{ __html: contentWithAnchors }}
          />

          {/* Share & CTA */}
          <div className="mt-16 pt-8 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <span className="text-slate-600">Partager cet article :</span>
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${window.location.origin}/blog/${article.slug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
                  data-testid="share-linkedin"
                >
                  <Share2 className="w-5 h-5 text-slate-600" />
                </a>
              </div>
              <Button 
                asChild 
                className="rounded-full bg-blue-600 hover:bg-blue-700"
                data-testid="article-cta"
              >
                <a href="mailto:mehdi.richa.formation@gmail.com?subject=Question suite à l'article : {article.title}">
                  Me contacter
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 bg-slate-50" data-testid="related-articles">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-serif text-2xl font-bold text-slate-900 mb-8 text-center">
              Articles similaires
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {relatedArticles.map((relatedArticle) => (
                <Link 
                  key={relatedArticle.id}
                  to={`/blog/${relatedArticle.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`related-article-${relatedArticle.id}`}
                >
                  <div className="relative h-48">
                    <img
                      src={relatedArticle.image}
                      alt={relatedArticle.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-sm text-blue-600 font-medium">{relatedArticle.category}</span>
                    <h3 className="font-serif text-lg font-bold text-slate-900 mt-2 group-hover:text-blue-600 transition-colors">
                      {relatedArticle.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Author Bio */}
      <section className="py-16 bg-white" data-testid="author-bio">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 rounded-2xl p-8">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-2xl font-bold">MR</span>
              </div>
              <div>
                <h3 className="font-serif text-xl font-bold text-slate-900 mb-2">
                  À propos de l'auteur
                </h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Mehdi Richa est formateur polyvalent et auditeur qualité dans le Vaucluse. 
                  Il accompagne les entreprises, organismes de formation et collectivités 
                  dans leurs projets de formation et d'amélioration continue.
                </p>
                <Link 
                  to="/a-propos"
                  className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center"
                >
                  En savoir plus
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogArticle;
